/// <reference path="../../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />
import * as React from 'react';
import { AppAudioBar } from './AppAudioBar';
import { AppAudioPlayerProps, IAudioPlayer, AppAudioPlayerFeatureName } from './AppAudioPlayer.props';
import { audioPlayerStackStyles, getAppAudioPlayerDropDownStyles, getAppAudioPlayerButtonStyles, getAppAudioPlayerRootStackCSSProperties, getAppAudioBarContainerCSSProperties, getVolumeCalloutContainersCSSProperties, getVolumeCalloutIconCSSProperties, getAppAudioPlayerTitleLinkStyles, getAppAudioPlayerTitleLabelStyles, getVolumeCalloutSliderStyles } from './AppAudioPlayer.styles';
import { Stack, Label, FontIcon, Link, DefaultButton, Dropdown, IDropdownOption, IIconProps, ResponsiveMode, Callout, Slider, DirectionalHint, initializeComponentRef } from 'office-ui-fabric-react';
import { AddTooltipToElement } from '../Tooltip/AddTooltipToElement';

interface AppAudioPlayerState {
  playing: boolean;
  currentTime: number;
  duration: number;
  playbackSpeed: number;
  showVolumeCallout: boolean;
  volume: number;
}

const defaultSeekJump = 5;

const playIcon: IIconProps = { iconName: 'Play' };
const pauseIcon: IIconProps = { iconName: 'Pause' };
const forwardIcon: IIconProps = { iconName: 'Next' };
const backwardIcon: IIconProps = { iconName: 'Previous' };
const volumeIcon: IIconProps = { iconName: 'Volume3' };

export class AppAudioPlayer extends React.PureComponent<AppAudioPlayerProps, AppAudioPlayerState> implements IAudioPlayer {
  private seekJump: number;
  private audioRef = React.createRef<HTMLAudioElement>();
  private volumeButtonRef = React.createRef<HTMLDivElement>();

  constructor(props: AppAudioPlayerProps) {
    super(props);

    initializeComponentRef(this);

    this.state = {
      playing: false,
      currentTime: 0,
      duration: 0,
      playbackSpeed: 1,
      showVolumeCallout: false,
      volume: 100
    };

    this.seekJump = this.props.seekJump ? this.props.seekJump : defaultSeekJump;
  }

  public seekTime = async (time: number): Promise<void> => {
    this.audioRef.current.currentTime = Math.max(Math.max(0, time), Math.min(0, this.state.duration));
    await this.audioRef.current.play();

    this.setState({
      currentTime: Math.max(Math.max(0, time), Math.min(0, this.state.duration)),
      playing: true
    });
  }

  public render() {
    const { currentTime, duration, playing, volume, showVolumeCallout } = this.state;
    const { audioSrc, audioPlayerTitle, audioPlayerTitleLink } = this.props;
    const playPauseButtonName = playing ? TranscriptionStrings.PauseButtonName : TranscriptionStrings.PlayButtonName;

    return (
      <Stack aria-label={TranscriptionStrings.AudioPlayerAccessibilityLabel} horizontalAlign="center" style={getAppAudioPlayerRootStackCSSProperties()}>
        <audio controls hidden
          ref={this.audioRef}
          onLoadedData={this._setAudioFileMetadata}
          onTimeUpdate={this._onTimeUpdate}
          onEnded={this._onPausePlayClicked}
          preload='auto'
        >
          <source src={audioSrc} type="audio/mp3" />
        </audio>
        {this._getAudioPlayerTitleElement(audioPlayerTitle, audioPlayerTitleLink)}
        <div style={getAppAudioBarContainerCSSProperties()}>
          <AppAudioBar type={'AppAudioBarProps'} currentTime={currentTime} duration={duration} onTimeChange={this.seekTime} />
        </div>
        <Stack horizontal horizontalAlign="center" styles={audioPlayerStackStyles}>
          {
            AddTooltipToElement(
              <Dropdown
                ariaLabel={TranscriptionStrings.PlaybackSpeedButtonName}
                options={[
                  { key: '0.5', text: '0.5x' },
                  { key: '1', text: '1x' },
                  { key: '1.25', text: '1.25x' },
                  { key: '1.5', text: '1.5x' },
                  { key: '2', text: '2x' }
                ]}
                defaultSelectedKey={this.state.playbackSpeed.toString()}
                responsiveMode={ResponsiveMode.large}
                onChange={this._onPlaybackSpeedChanged}
                dropdownWidth={75}
                styles={getAppAudioPlayerDropDownStyles()}
              />,
              TranscriptionStrings.PlaybackSpeedButtonName)
          }
          {AddTooltipToElement(<DefaultButton ariaLabel={TranscriptionStrings.RewindButtonName} onClick={this._onBackClicked} iconProps={backwardIcon} styles={getAppAudioPlayerButtonStyles()} />, TranscriptionStrings.RewindButtonName)}
          {AddTooltipToElement(
            <DefaultButton ariaLabel={playPauseButtonName}
              checked={playing}
              onClick={this._onPausePlayClicked}
              iconProps={playing ? pauseIcon : playIcon}
              styles={getAppAudioPlayerButtonStyles()}
            />, playPauseButtonName)}
          {AddTooltipToElement(<DefaultButton ariaLabel={TranscriptionStrings.ForwardButtonName} onClick={this._onForwardClicked} iconProps={forwardIcon} styles={getAppAudioPlayerButtonStyles()} />, TranscriptionStrings.ForwardButtonName)}

          <div ref={this.volumeButtonRef}>
            {AddTooltipToElement(<DefaultButton ariaLabel={TranscriptionStrings.VolumeButtonName} onClick={this._onShowVolumeClicked} styles={getAppAudioPlayerButtonStyles()} iconProps={volumeIcon} />, TranscriptionStrings.VolumeButtonName)}
          </div>
          {showVolumeCallout && (
            <Callout
              role="dialog"
              gapSpace={0}
              setInitialFocus={true}
              target={this.volumeButtonRef.current}
              directionalHint={DirectionalHint.bottomRightEdge}
              onDismiss={this._onVolumeCalloutDismiss}
              isBeakVisible={false}
            >
              <div style={getVolumeCalloutContainersCSSProperties()}>
                <FontIcon iconName={'Volume1'} style={getVolumeCalloutIconCSSProperties()} />
                <Slider styles={getVolumeCalloutSliderStyles()} min={0} max={100} defaultValue={volume} showValue={false} onChange={this._onVolumeChange} />
                <FontIcon iconName={'Volume3'} style={getVolumeCalloutIconCSSProperties()} />
              </div>
            </Callout>
          )}
        </Stack>
      </Stack>
    );
  }

  private _getAudioPlayerTitleElement(audioPlayerTitle: string, audioPlayerTitleLink: string) {
    if (audioPlayerTitle) {
      if (audioPlayerTitleLink) {
        return <Link styles={getAppAudioPlayerTitleLinkStyles()} href={audioPlayerTitleLink} target="_blank">{audioPlayerTitle}</Link>;
      } else {
        return <Label styles={getAppAudioPlayerTitleLabelStyles()}>{audioPlayerTitle}</Label>;
      }
    }

    return null;
  }

  private _onVolumeChange = (value: number): void => {
    if (this.props.onUserInteraction) {
      this.props.onUserInteraction(AppAudioPlayerFeatureName.VolumeChange);
    }

    this.audioRef.current.volume = value / 100;
    this.setState({
      volume: value
    });
  }

  private _onShowVolumeClicked = (): void => {
    if (this.props.onUserInteraction) {
      this.props.onUserInteraction(AppAudioPlayerFeatureName.ShowVolume);
    }

    this.setState({
      showVolumeCallout: !this.state.showVolumeCallout
    });
  }

  private _onVolumeCalloutDismiss = (): void => {
    this.setState({
      showVolumeCallout: false
    });
  };

  private _onPlaybackSpeedChanged = (_: React.FormEvent<HTMLDivElement>, option: IDropdownOption): void => {
    if (this.props.onUserInteraction) {
      this.props.onUserInteraction(AppAudioPlayerFeatureName.PlaybackSpeedChange);
    }

    const newPlaybackSpeed = parseFloat(option.key.toString());
    this.audioRef.current.playbackRate = newPlaybackSpeed;
    this.setState({
      playbackSpeed: parseFloat(option.key.toString())
    });
  }

  private _onBackClicked = (): void => {
    if (this.props.onUserInteraction) {
      this.props.onUserInteraction(AppAudioPlayerFeatureName.SeekBackward);
    }

    this.audioRef.current.currentTime = Math.max(0, this.audioRef.current.currentTime - this.seekJump);
  }

  private _onForwardClicked = (): void => {
    if (this.props.onUserInteraction) {
      this.props.onUserInteraction(AppAudioPlayerFeatureName.SeekForward);
    }

    this.audioRef.current.currentTime = Math.min(this.state.duration, this.audioRef.current.currentTime + this.seekJump)
  }

  private _onPausePlayClicked = async () => {
    if (this.state.playing) {
      if (this.props.onUserInteraction) {
        this.props.onUserInteraction(AppAudioPlayerFeatureName.PauseAudio);
      }

      this.audioRef.current.pause();
    } else {
      if (this.props.onUserInteraction) {
        this.props.onUserInteraction(AppAudioPlayerFeatureName.PlayAudio);
      }

      await this.audioRef.current.play();
    }

    this.setState({ playing: !this.state.playing });
  };

  private _onTimeUpdate = () => {
    if (this.props.onAudioPositionUpdated) {
      this.props.onAudioPositionUpdated(this.audioRef.current.currentTime);
    }

    this.setState({
      currentTime: this.audioRef.current.currentTime
    });
  }

  private _setAudioFileMetadata = () => {
    if (!this.audioRef) {
      return;
    }

    if (this.props.onAudioControlLoaded) {
      this.props.onAudioControlLoaded(this.audioRef.current.duration);
    }

    this.setState({
      currentTime: this.audioRef.current.currentTime,
      duration: this.audioRef.current.duration,
      volume: this.audioRef.current.volume * 100
    });
  };
}